import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const RewardApplication = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [month, setMonth] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const recordsPerPage = 10;

  useEffect(() => {
    fetchApplications();
  }, [currentPage, search, status, month]);

  const fetchApplications = async () => {
    try {
      const response = await apiService.post('rewardList', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: search,
        status: status,
        month: month
      });

      if (response.data) {
        setApplications(response.data);
        setTotalRecords(response.recordsFiltered || response.data.length);
        setTotalPages(Math.ceil((response.recordsFiltered || response.data.length) / recordsPerPage));
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const toggleEligible = async (id) => {
    try {
      const response = await apiService.put(`updateEligibleStatus/${id}`);
      if (response.success) {
        toast.success(response.message);
        fetchApplications();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong!');
      console.error(error);
    }
  };

  const deleteReward = async (id) => {
    if (window.confirm('Are you sure you want to delete this reward application?')) {
      try {
        const response = await apiService.delete(`deleteReward/${id}`);
        if (response.success) {
          toast.success(response.message);
          fetchApplications();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error('Something went wrong!');
        console.error(error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      APPLIED: 'bg-yellow-100 text-yellow-800',
      WINNER: 'bg-green-100 text-green-800',
      LOST: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="bg-white rounded-lg shadow-sm m-0">
        <div className="p-0">
          <div className="flex gap-4 mb-3">
            <div className="w-48">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="APPLIED">Applied</option>
                <option value="WINNER">Winner</option>
                <option value="LOST">Lost</option>
              </select>
            </div>
            <div className="w-48">
              <input
                type="month"
                value={month}
                onChange={(e) => { setMonth(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  placeholder="Search rewards..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[150px]">User</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[150px]">Query</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Month</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Status</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Eligible</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Applied Date</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  applications.map((app, index) => (
                    <tr key={app._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-4 py-3">
                        {app.userId ? (
                          <div>
                            <div className="font-semibold text-gray-900">
                              {app.userId.first_name} {app.userId.last_name}
                            </div>
                            {app.userId.business_name && (
                              <div className="text-xs text-gray-500">{app.userId.business_name}</div>
                            )}
                            <div className="text-xs text-gray-500">{app.userId.email}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {app.queryId ? (
                          <div>
                            <div className="font-semibold text-gray-900">#{app.queryId.id}</div>
                            {app.queryId.description && (
                              <div className="text-xs text-gray-500">
                                {app.queryId.description.substring(0, 50)}...
                              </div>
                            )}
                            <div className="text-xs text-gray-500">Budget: ₹{app.queryId.budget || 0}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{app.month || '--'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusBadge(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={app.eligible}
                            onChange={() => toggleEligible(app._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-gray-900 text-sm">
                        {formatDate(app.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => deleteReward(app._id)}
                          className="px-3 py-1 text-xs font-semibold rounded"
                          style={{ backgroundColor: '#ffe2e5', color: '#f1416c' }}
                        >
                          <i className="bi bi-trash text-sm"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? 'text-white'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={currentPage === page ? { backgroundColor: '#009ef7', borderColor: '#009ef7' } : {}}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardApplication;
